<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { ChevronLeft, ChevronRight, X, Trash2 } from 'lucide-vue-next'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import ImageViewer from './viewers/ImageViewer.vue'
import VideoViewer from './viewers/VideoViewer.vue'
import { escapeHtml } from '@/utils/strings'

const props = defineProps({
  resources: {
    type: Array,
    required: true
  },
  initialIndex: {
    type: Number,
    default: 0
  },
  open: {
    type: Boolean,
    default: false
  },
  showDelete: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:open', 'delete'])

const currentIndex = ref(props.initialIndex)

const currentResource = computed(() => {
  const resource = props.resources[currentIndex.value]
  if (!resource) return null

  // For images, ensure we use the fullsize variant if available
  if (resource.file_type === 'image' && resource.variant_urls) {
    return {
      ...resource,
      url: resource.variant_urls.fullsize || resource.variant_urls.original || resource.url
    }
  }

  return resource
})

const hasNext = computed(() => {
  return currentIndex.value < props.resources.length - 1
})

const hasPrevious = computed(() => {
  return currentIndex.value > 0
})

const totalCount = computed(() => {
  return props.resources.length
})

// Normalize resource type detection
const getResourceType = (resource) => {
  if (!resource) return null
  return resource.type || resource.file_type || null
}

const goToNext = () => {
  if (hasNext.value) {
    currentIndex.value++
  }
}

const goToPrevious = () => {
  if (hasPrevious.value) {
    currentIndex.value--
  }
}

const handleClose = () => {
  emit('update:open', false)
}

const handleDelete = () => {
  if (currentResource.value) {
    emit('delete', currentResource.value)
    // If this was the last resource, close the dialog
    if (props.resources.length === 1) {
      handleClose()
    } else if (currentIndex.value >= props.resources.length - 1) {
      // If we're at the last item, go to previous
      goToPrevious()
    }
  }
}

// Computed property for delete confirmation message
const deleteConfirmMessage = computed(() => {
  const filename = currentResource.value?.filename || 'this resource'
  const escaped = escapeHtml(filename)
  return `Are you sure you want to delete '${escaped}'? This action cannot be undone.`
})

// Keyboard navigation
const handleKeydown = (e) => {
  if (!props.open) return

  if (e.key === 'ArrowRight') {
    goToNext()
  } else if (e.key === 'ArrowLeft') {
    goToPrevious()
  } else if (e.key === 'Escape') {
    handleClose()
  }
}

// Reset index when resources or initialIndex changes
watch(() => props.initialIndex, (newIndex) => {
  currentIndex.value = newIndex
})

watch(() => props.open, (isOpen) => {
  if (isOpen) {
    currentIndex.value = props.initialIndex
  }
})

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<template>
  <Dialog :open="open" @update:open="handleClose">
    <DialogContent class="max-w-7xl w-[95vw] h-[90vh] p-0 overflow-hidden">
      <div class="relative w-full h-full flex flex-col bg-black">
        <!-- Header -->
        <div class="group/header absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/70 to-transparent p-4">
          <div class="flex items-center justify-between text-white">
            <div class="flex items-center gap-4">
              <span class="text-sm font-medium">
                {{ currentIndex + 1 }} / {{ totalCount }}
              </span>
              <span class="text-sm opacity-70">
                {{ currentResource?.filename || 'Media' }}
              </span>
            </div>
            <div class="flex items-center gap-2">
              <!-- Delete button (shows on hover) -->
              <DialogConfirm
                v-if="showDelete && currentResource"
                :title="'Delete Resource'"
                :description="deleteConfirmMessage"
                @confirm="handleDelete"
              >
                <button
                  @click.stop
                  class="p-2 bg-red-600/0 hover:bg-red-600/50 rounded-full transition-all duration-200 opacity-0 group-hover/header:opacity-100"
                  title="Delete resource"
                >
                  <Trash2 class="w-5 h-5 text-red-400" />
                </button>
              </DialogConfirm>
              <!-- Close button -->
              <button
                @click="handleClose"
                class="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X class="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <!-- Main content area -->
        <div class="flex-1 relative flex items-center justify-center overflow-hidden">
          <!-- Previous button -->
          <button
            v-if="hasPrevious"
            @click="goToPrevious"
            class="absolute left-4 z-10 p-3 bg-white/10 hover:bg-white/20 backdrop-blur rounded-full transition-colors"
            :aria-label="'Previous media'"
          >
            <ChevronLeft class="w-6 h-6 text-white" />
          </button>

          <!-- Media viewer with padding for controls -->
          <div class="absolute inset-0 flex items-center justify-center px-16 py-20">
            <!-- Image -->
            <img
              v-if="getResourceType(currentResource) === 'image'"
              :src="currentResource.url"
              :alt="currentResource.filename || 'Image content'"
              class="max-w-full max-h-full object-contain"
            />

            <!-- Video -->
            <video
              v-else-if="getResourceType(currentResource) === 'video'"
              :src="currentResource.url"
              controls
              class="max-w-full max-h-full object-contain"
            >
              Your browser does not support the video element.
            </video>

            <!-- Audio -->
            <div v-else-if="getResourceType(currentResource) === 'audio'" class="w-full max-w-2xl bg-white rounded-lg p-6">
              <audio
                :src="currentResource.url"
                controls
                class="w-full"
              >
                Your browser does not support the audio element.
              </audio>
              <p class="mt-4 text-center text-gray-600">
                {{ currentResource.filename || 'Audio file' }}
              </p>
            </div>
          </div>

          <!-- Next button -->
          <button
            v-if="hasNext"
            @click="goToNext"
            class="absolute right-4 z-10 p-3 bg-white/10 hover:bg-white/20 backdrop-blur rounded-full transition-colors"
            :aria-label="'Next media'"
          >
            <ChevronRight class="w-6 h-6 text-white" />
          </button>
        </div>

        <!-- Thumbnail strip at bottom -->
        <div class="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/70 to-transparent p-4">
          <div class="flex gap-2 justify-center overflow-x-auto max-w-full">
            <button
              v-for="(resource, index) in props.resources"
              :key="index"
              @click="currentIndex = index"
              class="flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all"
              :class="currentIndex === index ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'"
            >
              <!-- Thumbnail preview -->
              <div v-if="getResourceType(resource) === 'image'" class="w-full h-full">
                <img
                  :src="resource.variant_urls?.thumbnail || resource.url"
                  :alt="`Thumbnail ${index + 1}`"
                  class="w-full h-full object-cover"
                />
              </div>
              <div v-else-if="getResourceType(resource) === 'video'" class="w-full h-full bg-gray-700 flex items-center justify-center">
                <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                </svg>
              </div>
              <div v-else-if="getResourceType(resource) === 'audio'" class="w-full h-full bg-blue-600 flex items-center justify-center">
                <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                </svg>
              </div>
            </button>
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
